import {Button, ColumnLayout, ExpandableSection, FormField, Header, Input} from '@amzn/awsui-components-react';
import React, {useEffect, useState} from 'react';
import {isValidAcId, isValidFnsku, isValidNumericId} from '../../validation/InputValidator';
import ReactTooltip from 'react-tooltip';
import {MERCHANT_CUSTOMER_ID_TIP} from '../Content';
import Icon from "@amzn/awsui-components-react/polaris/icon";

interface AcSearchPanelProps {
    acId?: string;
    marketplaceId?: string;
    merchantCustomerId?: string;
    programId?: string;
    regionId?: string;
    fnsku?: string;
    searchHandler: Function;
}

const AcSearchPanel: React.FC<AcSearchPanelProps> = (props) => {
    const [acId, setAcId] = useState(props.acId || '');
    const [marketplaceId, setMarketplaceId] = useState(props.marketplaceId || '');
    const [merchantCustomerId, setMerchantCustomerId] = useState(props.merchantCustomerId || '');
    const [programId, setProgramId] = useState(props.programId || '');
    const [regionId, setRegionId] = useState(props.regionId || '');
    const [fnsku, setFnsku] = useState(props.fnsku || '');

    useEffect(() => {
        setAcId(props.acId || '');
        setMarketplaceId(props.marketplaceId || '');
        setMerchantCustomerId(props.merchantCustomerId || '');
        setProgramId(props.programId || '');
        setRegionId(props.regionId || '');
        setFnsku(props.fnsku || '');
    }, [props]);

    function executeSearchOnEnter(key: string): void {
        if (key === 'Enter') {
            props.searchHandler(acId, marketplaceId, merchantCustomerId, programId, regionId, fnsku);
        }
    }

    const AcKeyDisabled = !!acId;
    const AcIdDisabled = !!marketplaceId || !!merchantCustomerId || !!programId || !!regionId;

    const formBody = (
        <ColumnLayout columns={2}>
            <FormField label="Marketplace ID" stretch>
                <Input
                    id="acSearchPanel-marketplaceId"
                    invalid={marketplaceId.length > 0 && !isValidNumericId(marketplaceId)}
                    value={marketplaceId}
                    onChange={({detail}) => setMarketplaceId(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                    disabled={AcKeyDisabled}
                />
            </FormField>
            <FormField label="Program ID (optional)" stretch>
                <Input
                    id="acSearchPanel-programId"
                    value={programId}
                    onChange={({detail}) => setProgramId(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                    disabled={AcKeyDisabled}
                />
            </FormField>
            <FormField label={
                <span id="acSearchPanel-mcId-title" data-for="acSearchPanel-mcId-tooltip" data-tip={MERCHANT_CUSTOMER_ID_TIP}>
                    Merchant Customer ID <Icon name={'support'} />
                </span>
            } stretch>
                <Input
                    id="acSearchPanel-mcId"
                    invalid={merchantCustomerId.length > 0 && !isValidNumericId(merchantCustomerId)}
                    value={merchantCustomerId}
                    onChange={({detail}) => setMerchantCustomerId(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                    disabled={AcKeyDisabled}
                />
                <ReactTooltip id="acSearchPanel-mcId-tooltip" effect="solid" multiline={true} html={true} delayHide={3000} clickable={true}/>
            </FormField>
            <FormField label="Region ID (optional)" stretch>
                <Input
                    id="acSearchPanel-regionId"
                    value={regionId}
                    onChange={({detail}) => setRegionId(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                    disabled={AcKeyDisabled}
                />
            </FormField>
            <FormField label="AC ID" stretch>
                <Input
                    id="acSearchPanel-acId"
                    invalid={acId.length > 0 && !isValidAcId(acId)}
                    value={acId}
                    onChange={({detail}) => setAcId(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                    disabled={AcIdDisabled}
                />
            </FormField>
            <FormField label="FNSKU (optional)" stretch>
                <Input
                    id="acSearchPanel-fnsku"
                    invalid={fnsku.length > 0 && !isValidFnsku(fnsku)}
                    value={fnsku}
                    onChange={({detail}) => setFnsku(detail.value.trim())}
                    onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
                />
            </FormField>
            <Button variant="primary"
                    onClick={() => props.searchHandler(acId, marketplaceId, merchantCustomerId, programId, regionId, fnsku)}>
                Search
            </Button>
        </ColumnLayout>
    );

    return (
        <ExpandableSection
            id="acSearchPanel"
            header={<Header variant="h2">Search</Header>}
            variant="container"
            defaultExpanded>
            {formBody}
        </ExpandableSection>
    );
};

export default AcSearchPanel;
